"use client";

import { useEffect, useRef, useState } from "react";
import { useMessages } from "next-intl";

interface RichTextWithConsentProps {
  htmlContent: string;
  className?: string;
}

/**
 * Renders rich-text content that may contain YouTube videos. When the user
 * has not granted the OneTrust C0007 (targeting) consent group, each YouTube
 * iframe is replaced with a consent banner explaining how to enable cookies.
 */
const RichTextWithConsent = ({
  htmlContent,
  className = "",
}: RichTextWithConsentProps) => {
  const messages = useMessages();
  const cookieErrorTitle = messages["youtube-cookie-title"]
    ? messages["youtube-cookie-title"]
    : "YouTube cookie consent required";
  const cookieErrorDescription = messages["youtube-cookie-description"]
    ? messages["youtube-cookie-description"]
    : "Please accept marketing cookies to view this video.";
  const cookieErrorCTAText = messages["youtube-cookie-cta-text"]
    ? messages["youtube-cookie-cta-text"]
    : "Update Cookie Preferences";
  const cookieErrorBottomContent = messages["youtube-cookie-bottom-content"]
    ? messages["youtube-cookie-bottom-content"]
    : "";
  const [hasConsent, setHasConsent] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const checkConsent = () => {
      if (typeof window !== "undefined" && window.OptanonActiveGroups) {
        setHasConsent(window.OptanonActiveGroups.includes("C0007"));
      }
    };

    setTimeout(checkConsent);

    const handleConsentChange = () => checkConsent();
    window.addEventListener("OneTrustGroupsUpdated", handleConsentChange);

    const container = contentRef.current;
    const videoDivs = container.querySelectorAll("div.ecolab-youtube-video");

    videoDivs.forEach((div) => {
      const iframe = div.querySelector("iframe");
      if (!iframe) return;

      const src = iframe.getAttribute("src");
      if (!src?.includes("youtube.com")) return;

      let banner = div.querySelector(".youtube-consent-banner");
      if (!banner) {
        banner = document.createElement("div");
        banner.className =
          "lg:absolute youtube-consent-banner h-full w-full lg:top-0 flex flex-col justify-between flex-grow-1 bg-black lg:z-1 px-5 pt-[5px] pb-[10px]";
        banner.innerHTML = `
          <div>
            <h2 class="text-white text-[24px] mt-5 mb-[10px] font-medium leading-[1.1] pb-[10px]">
              ${cookieErrorTitle}
            </h2>
            <div class="text-white text-base richtext-content">
              ${cookieErrorDescription}
            </div>
          </div>
          <div class="flex justify-center min-h-[50px] items-center">
            <button
              type="button"
              id="ot-sdk-btn"
              class="ot-sdk-show-settings onetrust-yt-btn overflow-visible normal-case font-[inherit] !text-ecolab-blue border-2 !border-ecolab-blue !text-[14px] !font-bold !rounded-[20px] !bg-white hover:underline hover:!text-cookie-btn-blue"
            >${cookieErrorCTAText}</button>
          </div>
          <div class="text-white text-base richtext-content [&_a]:text-white [&_a:hover]:!text-link-blue-dark-bg [&_a:focus]:!text-link-blue-dark-bg [&_a]:underline [&_p]:mb-[5px]">
            ${cookieErrorBottomContent
              ?.replace("{##youtubeURL##}", src.toString())
              ?.replaceAll(String.raw`\"`, '"')}
           </div>
        `;
        div.appendChild(banner);
      }

      const bannerEl = banner as HTMLElement;
      if (hasConsent) {
        iframe.style.display = "block";
        bannerEl.style.display = "none";
        div.classList.add("cookieBanner");
      } else {
        iframe.style.display = "none";
        bannerEl.style.display = "flex";
        div.classList.remove("cookieBanner");
      }
    });

    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", handleConsentChange);
    };
  }, [
    hasConsent,
    cookieErrorTitle,
    cookieErrorDescription,
    cookieErrorBottomContent,
  ]);

  return (
    <div
      className={`text-[var(--text-base)] font-normal text-charcoal-black leading-[24px] tracking-[0.4px] lg:tracking-[0.8px] space-y-1 mb-5 wrap-anywhere richtext-content ${className}`}
      ref={contentRef}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default RichTextWithConsent;
